import { getEventPlayerName } from './normalize';

// Premier League / Opta stat type names — the keys we see on
// team_stat.data.type. `valueField` reads a sub-field on the stat row
// (used for half/interval splits); `derive` computes the value from two
// other rows; `sumTo100` forces the away side to 100-home instead of
// rounding each side independently. Without valueField/derive, the row's
// `value` is read directly.
//
// This is the FALLBACK config only — normally the app fetches the live,
// centrally-managed config from DWS (via DDS's getStatsConfig, see
// services/statsConfig.service.js) so AeDrive and Aether always show the
// identical stat list. This hardcoded copy is used only if that fetch
// fails or the backend hasn't been seeded yet, so the panel never renders
// empty. Keep it in sync with aedrive-ui/src/utils/teamStats.js's
// STATIC_STAT_CONFIG and with BB_DataWorldService's seed-stats-config.js —
// all three should carry the same 15 stats + Cards.
export const STATIC_STAT_CONFIG = [
    { id: 'possessionPercentage', type: 'possessionPercentage', label: 'Possession (%)', sumTo100: true },
    { id: 'possession_last5', type: 'possessionLast5', label: 'Possession (%) Last 5 Mins', sumTo100: true },
    { id: 'possession_last10', type: 'possessionLast10', label: 'Possession (%) Last 10 Mins', sumTo100: true },
    { id: 'possession_2h', type: 'possessionPercentage', label: '2nd Half Possession (%)', sumTo100: true, valueField: 'second_half_value' },
    { id: 'wonCorners', type: 'wonCorners', label: 'Corners' },
    { id: 'totalScoringAtt', type: 'totalScoringAtt', label: 'Attempts' },
    { id: 'totalScoringAtt_2h', type: 'totalScoringAtt', label: '2nd Half Attempts', valueField: 'second_half_value' },
    { id: 'ontargetScoringAtt', type: 'ontargetScoringAtt', label: 'Attempts on Target' },
    { id: 'distanceCovered', type: 'distanceRun', label: 'Distance Covered (KM)', divideBy: 1000 },
    { id: 'touchesInOppBox', type: 'touchesInOppBox', label: 'Touches in Opp. Box' },
    { id: 'totalPass', type: 'totalPass', label: 'Passes' },
    { id: 'passAccuracy', label: 'Pass Accuracy (%)', derive: 'passAccuracy', displayType: 'split-percentage' },
    { id: 'accuratePass', type: 'accuratePass', label: 'Completed Passes' },
    { id: 'finalThirdPasses', type: 'totalFinalThirdPasses', label: 'Final Third Passes' },
    { id: 'passesIntoFinalThird', type: 'finalThirdEntries', label: 'Passes into Final Third' },
    // Yellow/Red Cards are sourced from the AeDrive-authoritative *Opus rows
    // (totalYellowCardOpus / totalRedCardOpus) — written by aedrive's
    // handleCardAction in real time when an operator logs a card, not the
    // lagging Opta totals that arrive on a delay. Aether is read-only here;
    // aedrive owns every upsert. See BB_AeDrive_Web commit 56ac8eb.
    { id: 'totalYellowCard', type: 'totalYellowCardOpus', label: 'Yellow Cards' },
    { id: 'totalRedCard', type: 'totalRedCardOpus', label: 'Red Cards' },
];

// Team scores derived from the goal events on the timeline. The goal plugin
// (goal_event_object_builder.js) stores own-goal events with team_id already
// flipped to the scoring (benefiting) team, so we count by event.team_id
// directly — no further flip needed here. Mirrors aedrive-ui's
// txLiveHelpers.computeOpusGoalScores so both apps recompute the score from
// the full event list the same way instead of drifting from incremental
// ±1 patches (OPUS goal-delete inconsistency).
const GOAL_EVENT_KEYS = new Set(['goal', 'penalty', 'own_goal']);

export const computeOpusGoalScores = (events, homeTeamId, awayTeamId) => {
    let home = 0;
    let away = 0;
    for (const ev of (events || [])) {
        const key = (ev?.event_key || '').toLowerCase();
        if (!GOAL_EVENT_KEYS.has(key)) continue;
        if (ev?.team_id === homeTeamId) home += 1;
        else if (ev?.team_id === awayTeamId) away += 1;
    }
    return { home_team_score: home, away_team_score: away };
};

// Pass-accuracy derivation: accuratePass / totalPass * 100, rounded to a WHOLE
// number (no decimals) — identical to AeDrive's derivePassAccuracy so the two
// apps show the same value (the panels diverged: Aether used toFixed(1)).
const derivePassAccuracy = (statMap) => {
    const accurate = Number(statMap.get('accuratePass')?.value ?? 0);
    const total = Number(statMap.get('totalPass')?.value ?? 0);
    if (!total || !Number.isFinite(accurate) || !Number.isFinite(total)) return '0';
    return Math.round((accurate / total) * 100).toString();
};

const resolveStatValue = (statMap, cfg) => {
    if (cfg.derive === 'passAccuracy') return derivePassAccuracy(statMap);
    const row = statMap.get(cfg.type);
    if (!row) return '0';
    const raw = cfg.valueField ? (row[cfg.valueField] ?? '0') : (row.value ?? '0');
    if (cfg.sumTo100) {
        const n = Number(raw);
        return Number.isFinite(n) ? Math.round(n).toString() : '0';
    }
    if (cfg.divideBy) {
        const n = Number(raw);
        return Number.isFinite(n) ? (n / cfg.divideBy).toFixed(1) : '0';
    }
    return raw;
};

// Keep the full stat object (not just .value) so valueField lookups for
// half-totals can reach first_half_value / second_half_value.
const toStatsMap = (stats) =>
    new Map((stats || []).map((s) => [s.type, s]));

// True when the home side actually has a value for this stat (as opposed to
// missing data, which should fall back to independent left/right values
// rather than forcing a 100/0 split).
const hasStatValue = (statMap, cfg) => {
    const row = statMap.get(cfg.type);
    if (!row) return false;
    const raw = cfg.valueField ? row[cfg.valueField] : row.value;
    return raw != null;
};

export function buildStats(homeTeamStats, awayTeamStats, statConfig = STATIC_STAT_CONFIG) {
    const homeMap = toStatsMap(homeTeamStats);
    const awayMap = toStatsMap(awayTeamStats);

    return statConfig.map((cfg) => {
        const leftValue = resolveStatValue(homeMap, cfg);
        let rightValue = resolveStatValue(awayMap, cfg);

        // Possession-type splits must sum to exactly 100 — derive the away
        // side from 100 - home instead of rounding each side independently
        // (52.5/47.5 would otherwise show as 53/48 = 101%). Mirrors
        // aedrive-ui's teamStats.js TeamStatsBuilder#row.
        if (cfg.sumTo100 && hasStatValue(homeMap, cfg)) {
            const h = Number(leftValue);
            if (Number.isFinite(h)) rightValue = String(100 - h);
        }

        return {
            id: cfg.id,
            label: cfg.label,
            leftValue,
            rightValue,
            ...(cfg.displayType ? { displayType: cfg.displayType } : {}),
        };
    });
}

/**
 * Format a DDS event (from fixture.event) for the two-sided Events timeline.
 * Returns one entry per visual row (subs = 2 entries: sub_in, sub_out).
 * @param {Object} e - DDS event: event_id, event_key, team_id, player1_id, player2_id, time_in_seconds, updatedAt
 * @param {Object} homeTeam - fixture home_team[0] with player[]
 * @param {Object} awayTeam - fixture away_team[0] with player[]
 * @returns {Array<{ id: string, clockLabel: string, side: 'home'|'away', eventType: string, playerLabel: string, updatedAt?: string }>}
 */
export const formatDdsEventForTimeline = (e, homeTeam, awayTeam) => {
    const baseId = e.event_id || `ev-${e.time_in_seconds}-${Math.random().toString(36).slice(2)}`;
    const totalSeconds = Number(e.time_in_seconds) || 0;
    const minutes = Math.floor(totalSeconds / 60);
    const ss = String(totalSeconds % 60).padStart(2, '0');
    const clockLabel = minutes > 90 ? `90:00+${minutes - 90}:${ss}` : `${minutes}:${ss}`;

    // ✅ Combine all player sources
    const allPlayers = [
        ...(homeTeam?.players || []),
        ...(awayTeam?.players || []),
        ...(homeTeam?.subs || []),
        ...(awayTeam?.subs || []),
        ...(homeTeam?.others || []),
        ...(awayTeam?.others || []),
        ...(homeTeam?.originalPlayers || []),
        ...(awayTeam?.originalPlayers || []),
    ];

    // ✅ FIX: Match by multiple possible ID fields
    const playerById = (pid) => {
        if (!pid) return null;
        return allPlayers.find((p) =>
            p.player_id === pid ||
            p.id === pid ||
            p.lineup_id === pid
        );
    };

    // Event-row label: unique_name (disambiguation) > player_last_name.
    // Implemented in normalize.getEventPlayerName. Prefix with shirt number
    // when available so the timeline reads "10 - Messi".
    const playerLabel = (p) => {
        if (!p) return '';
        const shirtNum = p.shirt_number ?? '';
        const name = getEventPlayerName(p);
        return `${shirtNum} - ${name}`.trim().replace(/^- /, '').replace(/ -$/, '');
    };

    // ✅ FIX: Use teamId (camelCase)
    const homeTeamId = homeTeam?.teamId || homeTeam?.team_id;
    const side = e.team_id === homeTeamId ? 'home' : 'away';

    const p1 = e.player1_id ? playerById(e.player1_id) : null;
    const p2 = e.player2_id ? playerById(e.player2_id) : null;

    // ✅ Debug logging
    if (e.player1_id && !p1) {
        console.warn(`⚠️ Player not found for player1_id: ${e.player1_id}`);
    }
    if (e.player2_id && !p2) {
        console.warn(`⚠️ Player not found for player2_id: ${e.player2_id}`);
    }

    const key = (e.event_key || '').toLowerCase();
    const out = [];
    const updatedAt = e.updatedAt ?? e.updated_at;
    const timeInSeconds = Number(e.time_in_seconds) || 0;

    // ✅ FIX: Use event data as fallback for player name
    const getPlayerName = (player, fallbackId) => {
        if (player) return playerLabel(player);
        // Try to get name from event itself if available
        if (e.player1_name) return e.player1_name;
        if (e.player_name) return e.player_name;
        return fallbackId ? `Player ${fallbackId.slice(-4)}` : '';
    };

    // Raw fields carried through so the event edit modal can pre-populate form values.
    const rawFields = {
        player1_id: e.player1_id ?? null,
        player2_id: e.player2_id ?? null,
        event_type: e.event_type ?? null,
        team_id: e.team_id ?? null,
        event_key: e.event_key ?? null,
    };

    // Period markers (clock_start/end) → centered divider with the wall-clock
    // time of day, matching Drive's timeline. The @ae/event-timeline 2.3
    // component renders eventType:'period' rows (label + clockLabel). Mirrors
    // Drive's txLiveHelpers so both apps' timelines show the same period rows.
    const PERIOD_LABELS = {
        clock_start_1h: 'FIRST HALF · KICK OFF',
        clock_end_1h: 'FIRST HALF ENDED',
        clock_start_2h: 'SECOND HALF · KICK OFF',
        clock_end_2h: 'SECOND HALF ENDED',
    };
    if (PERIOD_LABELS[key]) {
        // event_id is "<key>_<epoch>"; show the trigger instant as local HH:MM:SS.
        const m = String(e.event_id ?? '').match(/_(\d{10,})$/);
        let wall = null;
        if (m) {
            const d = new Date(Number(m[1]));
            if (!Number.isNaN(d.getTime())) {
                wall = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
            }
        }
        out.push({
            id: baseId,
            clockLabel: wall ?? clockLabel,
            side,
            eventType: 'period',
            label: PERIOD_LABELS[key],
            updatedAt,
            timeInSeconds,
        });
        return out; // a clock transition is only ever a period row
    }

    if (key === 'goal' || key === 'own_goal' || key === 'penalty') {
        out.push({
            id: baseId,
            clockLabel,
            side,
            eventType: 'goal',
            playerLabel: getPlayerName(p1, e.player1_id) || 'Goal',
            updatedAt,
            timeInSeconds,
            goalSubtype: key,
            ...rawFields,
        });
    } else if (key === 'yellow_card' || key === 'red_card' || key === 'double_yellow' ||  key === 'double_yellow_card' ) {
        const eventType = key === 'red_card' ? 'red_card' : key === 'double_yellow' ? 'double_yellow' : 'yellow_card';
        out.push({
            id: baseId,
            clockLabel,
            side,
            eventType,
            playerLabel: getPlayerName(p1, e.player1_id),
            updatedAt,
            timeInSeconds,
            ...rawFields,
        });
    } else if (key === 'sub') {
        const onLabel = getPlayerName(p2, e.player2_id);
        const offLabel = getPlayerName(p1, e.player1_id);
        out.push(
            { id: `${baseId}-on`, clockLabel, side, eventType: 'sub_in', playerLabel: onLabel, updatedAt, timeInSeconds, ...rawFields },
            { id: `${baseId}-off`, clockLabel, side, eventType: 'sub_out', playerLabel: offLabel, updatedAt, timeInSeconds, ...rawFields }
        );
    } else if (key === 'additional_time') {
        // Standalone "ADDITIONAL TIME N'" divider (mirrors Drive's txLiveHelpers).
        // The announced minutes ride in event_name (no added_time column), so the
        // persisted name is the display label.
        out.push({
            id: baseId,
            clockLabel,
            side,
            eventType: 'added_time',
            label: e.event_name || 'ADDITIONAL TIME',
            updatedAt,
            timeInSeconds,
        });
    } else {
        out.push({
            id: baseId,
            clockLabel,
            side,
            eventType: 'other',
            playerLabel: getPlayerName(p1, e.player1_id) || (e.event_name || e.event_type || ''),
            updatedAt,
            timeInSeconds,
            ...rawFields,
        });
    }

    return out;
};